import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import CircleSpinner from "@redotech/redo-web/circle-spinner.svg";
import { useToolbarOverflow } from "@redotech/redo-web/components/plate-ui/toolbar-overflow";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useMemo, useState } from "react";

const AI_ICON_STYLE = { color: "var(--redo-colors-orange-dark-500)" };

const BusyIcon = () => <CircleSpinner style={AI_ICON_STYLE} />;
const StarIcon = () => <AiStarIcon style={AI_ICON_STYLE} />;

export interface AiMenuToolbarButtonProps {
  isGeneratingAiResponse?: boolean;
  isPolishingMessage?: boolean;
  onGenerateAiResponse?: () => void;
  onPolishMessage?: () => void;
}

export const AiMenuToolbarButton = memo(function AiMenuToolbarButton({
  isGeneratingAiResponse,
  isPolishingMessage,
  onGenerateAiResponse,
  onPolishMessage,
}: AiMenuToolbarButtonProps) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const { isOverflow } = useToolbarOverflow();
  const isBusy = isGeneratingAiResponse || isPolishingMessage;

  const items: RedoCommandMenuItem[] = useMemo(
    () => [
      {
        text: "Generate response",
        disabled: isBusy,
        shortcutHotkey: ["1"],
        onClick: () => {
          onGenerateAiResponse?.();
        },
      },
      {
        text: "Improve response",
        disabled: isBusy,
        shortcutHotkey: ["2"],
        onClick: () => {
          onPolishMessage?.();
        },
      },
    ],
    [isBusy, onGenerateAiResponse, onPolishMessage],
  );

  if (isOverflow) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          {isBusy ? (
            <CircleSpinner style={AI_ICON_STYLE} />
          ) : (
            <AiStarIcon style={AI_ICON_STYLE} />
          )}
          AI assist
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuItem
            disabled={isBusy}
            onSelect={() => onGenerateAiResponse?.()}
          >
            Generate response
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={isBusy}
            onSelect={() => onPolishMessage?.()}
          >
            Improve response
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <>
      <Tooltip title="AI assist">
        <RedoButton
          hierarchy="tertiary"
          IconLeading={isBusy ? BusyIcon : StarIcon}
          onClick={() => setOpen((prev) => !prev)}
          ref={setAnchor}
          size="sm"
          text=""
        />
      </Tooltip>
      <RedoCommandMenu
        anchor={anchor}
        items={items}
        open={open}
        placement="bottom-start"
        setOpen={setOpen}
      />
    </>
  );
});
